import { Icon } from "@mdi/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { mdiClose, mdiMagnify } from "@mdi/js";
import { Modal } from "antd";
import styled from "styled-components";

import { BaseButton } from "components/base";
import FieldErrorMessage from "components/base/FieldErrorMessage";
import { WELL_CARD_VIEW_SOURCE } from "components/well-card-panel/constants";

import { collectWellEntries } from "../utils/jumpToWells";
import { formatMatchedCount, useWellJump } from "./useWellJump";

interface WellJumpModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The Ctrl+A quick-entry dialogue: one input, paste a single API10 / UWI / well
 * name, Enter to fly there. A separate, lighter surface from the Jump to
 * Location "Well" tab, mirroring Insights; both call the same useWellJump() so
 * the two cannot drift. Pasting several identifiers still resolves them as a
 * set — the single-line input is an affordance, not a restriction.
 */
export default function WellJumpModal({ open, onClose }: WellJumpModalProps) {
  const [text, setText] = useState("");
  const inputRef = useRef<Nullable<HTMLInputElement>>(null);
  const { status, error, matched, submitJump, cancelPending, resetFeedback } =
    useWellJump(WELL_CARD_VIEW_SOURCE.quickWellJump);

  const entries = useMemo(() => collectWellEntries(text), [text]);
  const canSubmit = status !== "busy" && entries.length > 0;

  // Deferred a frame on purpose: antd's Modal moves focus to its own focus-trap
  // sentinel after mount, so focusing synchronously here is immediately undone.
  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open]);

  function handleClose() {
    // Reopening should not present the previous query.
    setText("");
    // Focus restoration is the OPENER's job: capturing document.activeElement
    // here races antd's focus trap, which moves focus to its sentinel on mount.
    // GoToTownshipPlay captures it in the hotkey handler instead.
    //
    // This component is NOT unmounted on close (destroyOnClose drops only antd's
    // children), so a submit still in flight would otherwise pass useWellJump's
    // stale-request check and fly the camera after the user walked away.
    // cancelPending, not clearJump: closing does not undo an earlier jump.
    cancelPending();
    onClose();
  }

  function handleChange(value: string) {
    setText(value);
    resetFeedback();
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    const fullMatch = await submitJump(text);
    if (fullMatch) {
      setText("");
      handleClose();
    }
  }

  return (
    <StyledModal
      open={open}
      onCancel={handleClose}
      destroyOnClose
      footer={null}
      // closable/footer off: the header and footer below carry the design
      // system's own chrome instead of antd's.
      closable={false}
      centered
      width={300}
      data-testid="jump-well-modal">
      <Shell>
        <header className="jump-modal-header">
          <span className="jump-modal-title">Enter API10/UWI</span>
          <button
            type="button"
            className="jump-modal-close"
            aria-label="Close"
            data-testid="jump-well-modal-close"
            onClick={handleClose}>
            <Icon path={mdiClose} size="16px" />
          </button>
        </header>

        <div className="jump-modal-body">
          <div className="jump-modal-input-row">
            <Icon className="jump-modal-search-icon" path={mdiMagnify} size="16px" />
            <input
              ref={inputRef}
              type="text"
              className="jump-modal-input"
              data-testid="jump-well-modal-input"
              aria-label="Enter API10, UWI, or exact well name"
              placeholder="API10, UWI, or exact well name"
              value={text}
              onChange={(event) => handleChange(event.target.value)}
              onKeyDown={(event) => {
                // A single-line input, so plain Enter submits — unlike the
                // Well tab's textarea, where Enter has to insert a newline.
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </div>
          {status === "busy" && <div className="jump-modal-hint">Looking up well…</div>}
          {matched && matched.matched !== matched.requested && (
            <div className="jump-modal-hint">{formatMatchedCount(matched)}</div>
          )}
          {error && <FieldErrorMessage message={error} />}
        </div>

        <footer className="jump-modal-footer">
          <BaseButton
            appearance="subtle"
            ellipses={false}
            data-testid="jump-well-modal-cancel"
            onClick={handleClose}>
            Cancel
          </BaseButton>
          <BaseButton
            appearance="default"
            ellipses={false}
            data-testid="jump-well-modal-go"
            onClick={handleSubmit}
            isDisabled={!canSubmit}>
            Go
          </BaseButton>
        </footer>
      </Shell>
    </StyledModal>
  );
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
    background: var(--color-surface-01);
    border: 1px solid var(--color-shadow);
    border-radius: var(--border-radius);
    /* No elevation token exists for a dialogue this size; kept as one local
       custom property rather than repeated down the rules below. */
    --jump-modal-elevation: 0 10px 28px rgba(4, 28, 44, 0.16);
    box-shadow: var(--jump-modal-elevation);
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 0;
  }
`;

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  font-family: var(--fontSans);
  color: var(--color-text);

  .jump-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 16px 11px;
    border-bottom: 1px solid var(--mist);
  }

  .jump-modal-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: var(--color-text);
  }

  .jump-modal-close {
    display: flex;
    align-items: center;
    padding: 0;
    border: none;
    background: none;
    color: var(--iconDefaultColor);
    cursor: pointer;
  }

  .jump-modal-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px 0;
  }

  .jump-modal-input-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    background: var(--white);
    border: 1px solid var(--cloud);
    border-radius: var(--border-radius);

    &:focus-within {
      border-color: var(--color-primary);
      box-shadow: var(--shadow-focus);
    }
  }

  .jump-modal-search-icon {
    flex-shrink: 0;
    color: var(--iconDefaultColor);
  }

  .jump-modal-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: none;
    font-family: var(--fontMono);
    font-size: 13px;
    color: var(--color-text);
  }

  .jump-modal-hint {
    font-size: 11.5px;
    color: var(--slate);
  }

  .jump-modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 16px;
  }
`;
